import { ShipmentTrackerStatus } from "@redotech/redo-model/fulfillments/outbound-shipment-label";
import { Order, Provider } from "@redotech/redo-model/order";
import {
  MerchantAppReturn,
  Product,
  ProductZod,
  Return,
  ReturnedProductStatus,
  ReturnType,
  ReviewingParty,
  shouldBeAManagedClaim,
} from "@redotech/redo-model/return";
import { ReturnStatus } from "@redotech/redo-model/return-status";
import { Team } from "@redotech/redo-model/team";
import { GetUser } from "@redotech/redo-model/user";
import { RedoBadgeColor } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { assertNever } from "@redotech/util/type";

export function answerName(answer: string) {
  return answer[0].toUpperCase() + answer.slice(1);
}

export function shipmentStatusName(status: string) {
  if (status === ReturnStatus.DELETED) {
    return "Reset";
  }
  if (!status) {
    return "-";
  }
  // EasyPost confirmed that "pre_transit" and "unknown" are interchangeable and can be treated as the same
  if (status === "pre_transit" || status === "unknown") {
    return "Pre-transit";
  }
  return status[0].toUpperCase() + status.slice(1).replace(/_/g, " ");
}

export function returnStatusName(status: ReturnStatus) {
  if (status === ReturnStatus.DELETED) {
    return "Reset";
  }
  return status[0].toUpperCase() + status.slice(1).replace(/_/g, " ");
}

export function returnKindNameProduct(
  product: ProductZod | Product,
  provision?: string,
  returnType?: string,
): string {
  const isExchange = isVariantExchange(product);
  if (returnType && returnType === "claim" && isExchange) {
    return "Replace";
  }
  if (isExchange) {
    return "Exchange";
  }
  return (
    product.strategy[0].toUpperCase() +
    product.strategy.slice(1).replace(/_/g, " ")
  );
}

export function returnKindNameReturn(
  return_: Return | MerchantAppReturn,
  abbreviate: boolean = true,
) {
  if (return_?.advancedExchangeItems?.length) {
    if (return_.provision === "instant") {
      return "Instant Exchange";
    } else {
      return "Exchange";
    }
  }
  const uniqueTypes = new Set(
    return_.products.map((product) =>
      returnKindNameProduct(product, return_.provision, return_.type),
    ),
  );
  const types = [...uniqueTypes].map((returnType) => {
    if (returnType === "Exchange" && return_.provision === "instant") {
      return "Instant Exchange";
    } else {
      return returnType;
    }
  });

  if (abbreviate) {
    let [type] = types.sort();
    if (1 < types.length) {
      type += " …";
    }
    return type;
  } else {
    return types.sort().join(", ");
  }
}

export function isVariantExchange(product: Product | ProductZod): boolean {
  return !!product.exchange_for || !!product.exchangeGroupItem;
}

export type ItemDispositionModalOpen =
  | { open: true; item: ProductZod }
  | { open: false };

export const returnStatusToBadgeColor: Record<ReturnStatus, RedoBadgeColor> = {
  [ReturnStatus.COMPLETE]: "success",
  [ReturnStatus.DELIVERED]: "blue",
  [ReturnStatus.IN_TRANSIT]: "pink",
  [ReturnStatus.NEEDS_REVIEW]: "blue",
  [ReturnStatus.IN_REVIEW]: "orange",
  [ReturnStatus.OPEN]: "warning",
  [ReturnStatus.REJECTED]: "error",
  [ReturnStatus.DELETED]: "warning",
  [ReturnStatus.FLAGGED]: "orange",
  [ReturnStatus.PRE_SHIPMENT]: "blueLight",
};

export function itemStatusName(
  productStatus: ReturnedProductStatus,
  returnStatus: ReturnStatus,
) {
  if (returnStatus === ReturnStatus.DELETED) {
    return "Reset";
  }
  switch (productStatus) {
    case ReturnedProductStatus.COMPLETE:
      return "Processed";
    case ReturnedProductStatus.REJECTED:
      return "Rejected";
    case ReturnedProductStatus.PENDING:
      return "Pending";
    case ReturnedProductStatus.PRE_SHIPMENT:
      return "Pre-Shipment";
    case ReturnedProductStatus.APPROVED:
    case ReturnedProductStatus.IN_REVIEW:
    case ReturnedProductStatus.OPEN:
      return "Unprocessed";
    default:
      assertNever(productStatus);
  }
}

export function orderStatusName(shopifyFulfillmentStatus: string) {
  switch (shopifyFulfillmentStatus) {
    case null:
      return "Unfulfilled";
    case "partial":
      return "Partially fulfilled";
    default:
      return (
        shopifyFulfillmentStatus[0].toUpperCase() +
        shopifyFulfillmentStatus.slice(1)
      );
  }
}

export function orderExternalUrl(
  storeUrl: string,
  order: Order,
): string | undefined {
  switch (order.provider) {
    case Provider.COMMENTSOLD: {
      const url = new URL("https://commentsold.com/admin/orders");
      url.searchParams.set("order", order.shopify.name.replace("CS-", ""));
      url.searchParams.set("scanned", "false");
      return url.toString();
    }
    case Provider.SHOPIFY:
      return `https://${storeUrl}/admin/orders/${order.shopify_id || ""}`;
  }
  return undefined;
}

export function isExchangeOrderEditable(
  return_: MerchantAppReturn,
  { canEditReturn }: { canEditReturn: boolean },
) {
  const isProcessed = [ReturnStatus.COMPLETE, ReturnStatus.REJECTED].includes(
    return_.status,
  );
  const isReset = [ReturnStatus.DELETED].includes(return_.status);
  const exchangeOrderExists =
    !!return_.exchangeOrder?.length || !!return_.draftOrderId;

  return (
    canEditReturn &&
    !return_.instantRefund &&
    !isProcessed &&
    !isReset &&
    !exchangeOrderExists
  );
}

export function hasNewOrderItems(return_: Return) {
  return (
    return_.products.some((product) => isVariantExchange(product)) ||
    return_.advancedExchangeItems.length > 0
  );
}

export const returnTypeName = (returnType?: ReturnType, titleCase = false) =>
  titleCase ? (returnType === "return" ? "Return" : "Claim") : returnType;

export function getProductsByShippingGroupTitle(return_: MerchantAppReturn) {
  const productsByGroupName: { groupName: string; products: ProductZod[] }[] =
    [];

  if (return_?.shipmentGroups?.length) {
    for (const product of return_.products) {
      const allGroups = return_.shipmentGroups;
      const shipmentGroupsIDs = product.shipmentGroupIDs?.length
        ? product.shipmentGroupIDs
        : product.shipmentGroupID
          ? [product.shipmentGroupID]
          : [];
      const shipmentGroups = return_.shipmentGroups.filter((group) =>
        shipmentGroupsIDs.includes(group.id),
      );
      const shipmentIndices: number[] = shipmentGroups
        .map((group) => allGroups.indexOf(group))
        .sort();

      const groupName = `Shipment ${shipmentIndices.map((ind) => ind + 1).join(" + ")}`;

      const productGroup = productsByGroupName.find(
        (group) => group.groupName === groupName,
      );
      if (productGroup) {
        productGroup?.products.push(product);
      } else {
        productsByGroupName.push({ groupName, products: [product] });
      }
    }
  }

  return productsByGroupName;
}

/**
 * Returns true if the claim either owned by the merchant or the user has
 * manage managed claim permissions.
 */
export function showManagedClaimActions(
  return_?: MerchantAppReturn,
  user?: GetUser,
  team?: Team,
) {
  if (!return_ || !user || !team) {
    return true;
  }
  if (!team.settings?.packageProtection?.newClaimsTables) {
    return true;
  }
  const shouldBeManagedClaim_ = shouldBeAManagedClaim({
    returnType: return_.type,
    packageProtectionPlusEnabled:
      !!team?.settings?.packageProtection?.packageProtectionPlusEnabled,
  });
  if (!shouldBeManagedClaim_) {
    return true;
  }
  const isClaimOwnedByMerchant =
    return_.reviewingParty === ReviewingParty.MERCHANT;
  const isManagedClaimAdmin = user.email
    .trim()
    .toLowerCase()
    .endsWith("@getredo.com");
  return (
    isClaimOwnedByMerchant || (shouldBeManagedClaim_ && isManagedClaimAdmin)
  );
}

/**
 * Checks if a return has any shipments that are stuck in transit
 * Uses the same logic as the stuck in transit filter on the returns table:
 * 1. Return status must be one of: OPEN, IN_TRANSIT, DELIVERED, NEEDS_REVIEW, IN_REVIEW, FLAGGED
 * 2. Must have at least one shipment that:
 *    - Has a tracker status of IN_TRANSIT, OUT_FOR_DELIVERY, ERROR, or FAILURE
 *    - Has a last updated date more than 10 days ago
 */
export function isReturnStuckInTransit(return_: MerchantAppReturn): {
  isStuck: boolean;
  stuckShipmentCount: number;
} {
  if (!return_.shipments || return_.shipments.length === 0) {
    return { isStuck: false, stuckShipmentCount: 0 };
  }

  // First check if the return status matches the filter criteria
  const stuckReturnStatuses = [
    ReturnStatus.OPEN,
    ReturnStatus.IN_TRANSIT,
    ReturnStatus.DELIVERED,
    ReturnStatus.NEEDS_REVIEW,
    ReturnStatus.IN_REVIEW,
    ReturnStatus.FLAGGED,
  ];
  const hasStuckReturnStatus = stuckReturnStatuses.includes(return_.status);

  if (!hasStuckReturnStatus) {
    return { isStuck: false, stuckShipmentCount: 0 };
  }

  // Check if the shipment is in a stuck status
  const stuckShipmentStatuses = [
    ShipmentTrackerStatus.IN_TRANSIT,
    ShipmentTrackerStatus.OUT_FOR_DELIVERY,
    ShipmentTrackerStatus.ERROR,
    ShipmentTrackerStatus.FAILURE,
  ];

  let stuckShipmentCount = 0;

  // Check each shipment for stuck trackers
  for (const shipment of return_.shipments) {
    const tracker = shipment._shipment?.tracker;

    if (!tracker?.status) {
      continue;
    }

    const shipmentStatus = tracker.status;
    const lastUpdated = tracker.updated_at;

    if (
      stuckShipmentStatuses.includes(shipmentStatus as ShipmentTrackerStatus) &&
      lastUpdated
    ) {
      // Check if that shipment hasn't been updated in the last 10 days
      const lastUpdatedDate = new Date(lastUpdated);
      const tenDaysAgo = new Date();
      tenDaysAgo.setDate(tenDaysAgo.getDate() - 10);

      if (lastUpdatedDate < tenDaysAgo) {
        stuckShipmentCount++;
      }
    }
  }

  const isStuck = stuckShipmentCount > 0;

  return { isStuck, stuckShipmentCount };
}
